import { Injectable, inject, signal, computed } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { ES_AUTH_POPUP } from './auth-popup';
import { ES_AUTH_CONFIG, buildOAuthConfig } from './auth.config';
import { ES_ROLES_LOADER } from './roles-loader';

/**
 * Pull org-scoped role names out of the Phase Two `organizations` token claim,
 * shaped `{ "<orgId>": { name, roles: [...] } }`. Prefers the caller's own org
 * (`org_id`); falls back to the union across all orgs when that key isn't
 * present (e.g. the claim keys by a different id). Returns `[]` for anything
 * malformed — role parsing must never break login.
 */
export function extractOrgRoles(claim: unknown, organizationId?: string): string[] {
  if (!claim || typeof claim !== 'object') return [];
  const orgs = claim as Record<string, { roles?: unknown }>;
  const rolesOf = (entry?: { roles?: unknown }): string[] =>
    Array.isArray(entry?.roles) ? entry!.roles.filter((r): r is string => typeof r === 'string') : [];

  if (organizationId && orgs[organizationId]) return rolesOf(orgs[organizationId]);
  return Array.from(new Set(Object.values(orgs).flatMap(rolesOf)));
}

/** First org id (key) in the Phase Two `organizations` claim, or undefined. */
export function firstOrgId(claim: unknown): string | undefined {
  if (!claim || typeof claim !== 'object') return undefined;
  return Object.keys(claim as Record<string, unknown>)[0];
}

export interface AuthUser {
  id: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  roles: string[];
  organizationId?: string;
  /** Preferred UI language (Keycloak `locale` attribute), if the realm has i18n on. */
  locale?: string;
}

/**
 * Shared auth service — works for both admin SPA (browser redirect)
 * and starter (Electron BrowserWindow redirect).
 *
 * Uses angular-oauth2-oidc with Authorization Code + PKCE flow.
 * Keycloak handles all UI (login, register, reset, MFA).
 *
 * State exposed as signals for reactive binding.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly oauthService = inject(OAuthService);
  private readonly config = inject(ES_AUTH_CONFIG);
  /** Optional app hook to fetch roles not carried in the token (e.g. org-scoped roles). */
  private readonly rolesLoader = inject(ES_ROLES_LOADER, { optional: true });
  /** Optional popup runner — login/register/logout open in it instead of redirecting the app. */
  private readonly popup = inject(ES_AUTH_POPUP, { optional: true });

  private readonly _user = signal<AuthUser | null>(null);
  private readonly _initialized = signal(false);
  /**
   * Memoised init run. The app shell (`AppComponent.ngOnInit`) and the route
   * guard both call `init()`; without this, two concurrent
   * `loadDiscoveryDocumentAndTryLogin()` runs each POST the single-use
   * authorization `code` — the second fails, the login is dropped, and the
   * guard bounces back to Keycloak: an endless redirect loop.
   */
  private initPromise: Promise<void> | null = null;
  /** Set by register(): the next authorize URL goes to Keycloak's registration page. */
  private registrationPending = false;

  readonly user = this._user.asReadonly();
  readonly accessToken = computed(() => this.oauthService.getAccessToken());
  readonly initialized = this._initialized.asReadonly();

  /**
   * Whether a valid access token is held. A plain method, not a signal: token
   * validity isn't signal state (a `computed` here never recomputed — it kept
   * its first answer across a later login).
   */
  isAuthenticated(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  /**
   * Initialize OAuth — discover endpoints, complete any auth-code callback,
   * set up token refresh. Idempotent: the app shell and the route guard both
   * call this, so the work — and the single-use code exchange — runs once.
   */
  init(): Promise<void> {
    this.initPromise ??= this.doInit().catch((err: unknown) => {
      // A failed init must not poison the service — drop the memo so a
      // later call (or a reload) can retry cleanly.
      this.initPromise = null;
      throw err;
    });
    return this.initPromise;
  }

  private async doInit(): Promise<void> {
    const oauthConfig = buildOAuthConfig(this.config);
    oauthConfig.openUri = (uri) => this.open(uri);
    this.oauthService.configure(oauthConfig);

    this.oauthService.events.subscribe(event => {
      if (event.type === 'token_received' || event.type === 'token_refreshed') {
        // Re-load the profile (which resets roles to the token's realm roles),
        // then re-merge any org-scoped roles so a silent refresh doesn't drop them.
        void this.loadUserProfile().then(() => this.augmentRoles());
      }
      if (event.type === 'logout' || event.type === 'session_terminated') {
        this._user.set(null);
      }
    });

    await this.oauthService.loadDiscoveryDocumentAndTryLogin();
    this.oauthService.setupAutomaticSilentRefresh();

    if (this.oauthService.hasValidAccessToken()) {
      await this.loadUserProfile();
      // Merge non-token roles before init resolves — guards/nav read roles
      // right after, so they must see the org-scoped roles too.
      await this.augmentRoles();
    }

    this._initialized.set(true);
  }

  /**
   * Merge roles from the optional `ES_ROLES_LOADER` into the current user.
   * Best-effort: a failure leaves the realm-role-only `roles` intact rather
   * than blocking login. No-op when no loader is provided or no user is set.
   */
  private async augmentRoles(): Promise<void> {
    const user = this._user();
    if (!user || !this.rolesLoader) return;
    try {
      const extra = await this.rolesLoader.loadRoles(user);
      if (extra.length === 0) return;
      this._user.update((u) =>
        u ? { ...u, roles: Array.from(new Set([...u.roles, ...extra])) } : u);
    } catch {
      // Best-effort — gating falls back to the token's realm roles.
    }
  }

  /**
   * Redirect to Keycloak login page. `params` go onto the authorize URL —
   * e.g. `{ kc_action: 'UPDATE_PASSWORD' }`, `login_hint`, `ui_locales`.
   */
  login(params: Record<string, string> = {}): void {
    this.oauthService.initCodeFlow('', params);
  }

  /**
   * Redirect to the Keycloak registration page; returns signed in like login().
   */
  register(params: Record<string, string> = {}): void {
    this.registrationPending = true;
    this.oauthService.initCodeFlow('', params);
  }

  /** Logout — revoke token and redirect. */
  logout(): void {
    this.oauthService.logOut();
    this._user.set(null);
  }

  /**
   * Every Keycloak navigation (authorize, registration, end-session) goes
   * through here: a redirect of the app, or the popup when one is provided.
   */
  private open(uri: string): void {
    if (this.registrationPending) {
      // Keycloak's `/registrations` takes the authorize params — `prompt=create`
      // only exists from Keycloak 26.1 on (we run 26.0).
      this.registrationPending = false;
      uri = uri.replace('/protocol/openid-connect/auth?', '/protocol/openid-connect/registrations?');
    }
    if (this.popup) void this.openInPopup(this.popup, uri);
    else location.href = uri;
  }

  /**
   * Run a Keycloak page in the popup; when it lands on the redirect URI with a
   * code, exchange it here (the PKCE verifier lives in this window's storage).
   * `token_received` then loads the profile. A closed popup or a logout
   * callback (no code) needs nothing further.
   */
  private async openInPopup(popup: NonNullable<typeof this.popup>, uri: string): Promise<void> {
    const result = await popup(uri);
    if (!result) return;
    const query = new URL(result).search;
    if (!new URLSearchParams(query).has('code')) return;
    // The app's own URL carries no code here — leave it alone.
    await this.oauthService.tryLoginCodeFlow({ customHashFragment: query, preventClearHashAfterLogin: true });
  }

  /** Get the raw access token for HTTP interceptor. */
  getAccessToken(): string | null {
    return this.oauthService.getAccessToken() ?? null;
  }

  private async loadUserProfile(): Promise<void> {
    try {
      const claims = this.oauthService.getIdentityClaims() as Record<string, unknown>;
      if (!claims) return;

      const realmRoles = ((claims['realm_access'] as Record<string, string[]>)?.['roles']) ?? [];
      // The org id isn't a top-level claim — it's the key of the Phase Two
      // `organizations` claim. Fall back to `org_id` if a realm ever sets it.
      const organizationId =
        (claims['org_id'] as string | undefined) ?? firstOrgId(claims['organizations']);
      const orgRoles = extractOrgRoles(claims['organizations'], organizationId);

      this._user.set({
        id: claims['sub'] as string,
        username: (claims['preferred_username'] ?? claims['email']) as string,
        email: claims['email'] as string,
        firstName: claims['given_name'] as string | undefined,
        lastName: claims['family_name'] as string | undefined,
        // Realm roles + Phase Two org-scoped roles (super-admin / editors / staff),
        // which ride in the `organizations` claim, not `realm_access`.
        roles: Array.from(new Set([...realmRoles, ...orgRoles])),
        organizationId,
        locale: claims['locale'] as string | undefined,
      });
    } catch {
      // Silent — profile load is best-effort
    }
  }
}
